import { useEffect, useState } from 'react';
import mammoth from 'mammoth';

/**
 * Document preview component with HTML rendering
 */
const DocumentPreview = ({ documentBuffer, formData, placeholders }) => {
  const [previewHtml, setPreviewHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentBuffer) return;

    const generatePreview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Convert the filled buffer to HTML for preview
        const result = await mammoth.convertToHtml({ arrayBuffer: documentBuffer });

        // Replace placeholders that might still be visible in HTML
        let html = result.value;

        // Replace any remaining placeholders with form data
        placeholders.forEach((placeholder) => {
          const value = formData[placeholder] || `\${${placeholder}}`;
          // Escape special characters for regex
          const escapedPlaceholder = placeholder.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
          // eslint-disable-next-line no-useless-escape
          const regex = new RegExp(`\\\$\\{${escapedPlaceholder}\\}`, 'g');
          html = html.replace(regex, `<strong>${value}</strong>`);
        });

        setPreviewHtml(html);
      } catch (err) {
        console.error('Error generating preview:', err);
        setError('Failed to generate document preview');
      } finally {
        setIsLoading(false);
      }
    };

    generatePreview();
  }, [documentBuffer, formData, placeholders]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange border-t-transparent" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Generating preview...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="bg-white dark:bg-gray-800 p-0">
        <h3 className="text-sm font-bold text-[#383838] dark:text-white mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-orange text-base">description</span>
          Filled Document Preview
        </h3>

        <div className="bg-[#F9F9F9] dark:bg-gray-900 rounded-xl p-8 max-h-[500px] overflow-y-auto border border-[#F2F2F2] dark:border-gray-700 custom-scrollbar">
          <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-headings:mb-2" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>

        <div className="mt-4 p-4 bg-orange/5 dark:bg-orange/10 rounded-lg border border-orange/10 dark:border-orange/20 flex items-start gap-3">
          <span className="material-symbols-outlined text-orange text-lg">info</span>
          <p className="text-[11px] text-[#383838] dark:text-gray-300 leading-relaxed">
            <strong className="text-orange">Important:</strong> This is an automatic preview generated for your review. While we preserve content and basic structure, complex Word formatting like
            custom fonts or specific margins might appear slightly different here than in the final .docx file.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
